import { useTranslation } from 'react-i18next'

import { useQuery } from '@tanstack/react-query'

import type { ConfigStore, Product, ProductAvailability, ProductCategory, ProductAvailabilities } from './api.types'
import useApi from './useApi'

export const getCategoryId = (category: ProductCategory | undefined): number | string | null => {
  const typedCategory = category as { id?: unknown; categoryId?: unknown; productCategoryId?: unknown } | undefined
  const id = typedCategory?.id ?? typedCategory?.categoryId ?? typedCategory?.productCategoryId
  return typeof id === 'number' || typeof id === 'string' ? id : null
}

const mapConfigToLegacyAppData = (config: ConfigStore | null) => {
  if (!config) {
    return null
  }

  const additional = config.additionalConfiguration || {}
  return {
    ...config,
    ...additional,
    deviceId: config.deviceId,
    active: config.active,
    defaultLanguage: config.defaultLanguage,
    currency: config.currency,
    sessionTimeoutSeconds: config.sessionTimeoutSeconds,
    additionalConfiguration: additional
  }
}

export const useAppQuery = (options?: Omit<Parameters<typeof useQuery>[0], 'queryKey' | 'queryFn'>) => {
  const { config } = useApi()

  return useQuery({
    queryKey: ['app'],
    queryFn: async () => {
      const data = await config()
      return mapConfigToLegacyAppData(data)
    },
    ...options
  })
}

interface TicketsQuery {
  products: Product[]
}

export const useCategoriesQuery = (options?: Omit<Parameters<typeof useQuery<ProductCategory[]>>[0], 'queryKey' | 'queryFn'>) => {
  const { i18n } = useTranslation()
  const { getProductCategories } = useApi()
  const langCode = i18n.language

  return useQuery<ProductCategory[]>({
    queryKey: ['categories', langCode],
    refetchOnMount: 'always',
    gcTime: 0,
    queryFn: async () => {
      const categories = await getProductCategories()
      return categories
    },
    ...options
  })
}

export const useTicketsQuery = (
  categoryId: number | string | null,
  options?: Omit<Parameters<typeof useQuery<TicketsQuery>>[0], 'queryKey' | 'queryFn' | 'enabled'>
) => {
  const { i18n } = useTranslation()
  const { getProducts } = useApi()
  const langCode = i18n.language

  return useQuery<TicketsQuery>({
    queryKey: ['tickets', categoryId, langCode],
    enabled: categoryId !== null,
    refetchOnMount: 'always',
    gcTime: 0,
    queryFn: async () => {
      if (categoryId === null) {
        return { products: [] }
      }

      const products = await getProducts(categoryId)
      return { products: products.filter(o => o.consumerCategories?.length > 0) }
    },
    ...options
  })
}

const formatDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const useCategoryAvailabilityQuery = (
  categoryId: number | string | null,
  options?: Omit<Parameters<typeof useQuery<ProductAvailabilities[]>>[0], 'queryKey' | 'queryFn' | 'enabled'>
) => {
  const { getCategoryAvailability } = useApi()
  const today = formatDate(new Date())

  return useQuery<ProductAvailabilities[]>({
    queryKey: ['categoryAvailability', categoryId, today],
    enabled: categoryId !== null,
    refetchOnMount: 'always',
    gcTime: 0,
    queryFn: async () => {
      if (categoryId === null) {
        return []
      }

      return getCategoryAvailability({
        categoryId,
        fromDate: today,
        toDate: today
      })
    },
    ...options
  })
}

export const useAvailabilityQuery = (
  categoryId: number | string | null,
  productId: number | string | null,
  _options?: Omit<Parameters<typeof useQuery<ProductAvailability[]>>[0], 'queryKey' | 'queryFn' | 'enabled'>
) => {
  const query = useCategoryAvailabilityQuery(categoryId, { refetchOnMount: false })
  const data: ProductAvailability[] =
    productId === null
      ? []
      : query.data?.find(entry => String(entry.productId) === String(productId))?.availability ?? []

  return {
    ...query,
    data
  }
}
